import { Link, router, useForm } from "@inertiajs/react"
import { SearchLg, Send01 } from "@untitledui/icons"
import { useState } from "react"

import { Badge, StatusBadge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { Panel } from "@/components/ui/panel"
import { formatRelative } from "@/lib/format"
import {
  adminEmailMessagesPath,
  dryRunAdminEmailHealthPath,
  smokeAdminEmailHealthPath,
  testSendAdminEmailHealthPath,
} from "@/routes"

import {
  type DryRunResult,
  OUTCOME_COLOR,
  OUTCOME_LABEL,
  type Triggers,
} from "../types"

/**
 * The page's primary affordance: start from an address, end at a verdict.
 * One input feeds both the routing dry run (answers "what would happen now")
 * and the test send (makes it happen, through the ordinary guarded path).
 * The ledger link from the verdict answers "what happened then".
 */
export function AddressCheck({
  dryRun,
  triggers,
  webhooksExpected,
}: {
  dryRun: DryRunResult | null
  triggers: Triggers
  webhooksExpected: boolean
}) {
  const form = useForm({ address: dryRun?.address ?? "" })
  const [sending, setSending] = useState(false)
  const [smoking, setSmoking] = useState(false)

  // Buttons stay enabled while a request is in flight — a disabling button
  // drops keyboard focus mid-action — so every handler guards re-entry.
  const check = () => {
    if (form.processing) return
    form.post(dryRunAdminEmailHealthPath(), { preserveScroll: true })
  }

  const sendTest = () => {
    if (sending || form.data.address.trim() === "") return
    router.post(
      testSendAdminEmailHealthPath(),
      { address: form.data.address },
      {
        preserveScroll: true,
        onStart: () => setSending(true),
        onFinish: () => setSending(false),
      },
    )
  }

  const runSmoke = () => {
    if (smoking || triggers.smoke_blockers.length > 0) return
    router.post(
      smokeAdminEmailHealthPath(),
      {},
      {
        preserveScroll: true,
        onStart: () => setSmoking(true),
        onFinish: () => setSmoking(false),
      },
    )
  }

  return (
    <Panel
      title="Check an address"
      description="Checks how mail to this address would route right now: suppression list first, then the allowlist. Nothing sends and nothing is recorded."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          check()
        }}
      >
        <Input
          aria-label="Address to check"
          placeholder="someone@school.edu.au"
          size="sm"
          className="w-80"
          icon={SearchLg}
          value={form.data.address}
          onChange={(value: string) => form.setData("address", value)}
        />
        <Button
          type="submit"
          color="primary"
          size="md"
          isLoading={form.processing}
          showTextWhileLoading
        >
          Check routing
        </Button>
        {triggers.available && (
          <Button
            color="secondary"
            size="md"
            iconLeading={Send01}
            isLoading={sending}
            showTextWhileLoading
            onPress={sendTest}
          >
            Send test email
          </Button>
        )}
      </form>

      {dryRun && (
        <div
          role="status"
          className="border-secondary flex flex-col gap-2 border-t pt-3"
        >
          <div className="flex flex-wrap items-center gap-2">
            {dryRun.address && (
              <span className="text-primary text-sm font-medium">
                {dryRun.address}
              </span>
            )}
            <StatusBadge tier={OUTCOME_COLOR[dryRun.outcome]}>
              {OUTCOME_LABEL[dryRun.outcome]}
            </StatusBadge>
          </div>
          <p className="text-secondary text-sm">{dryRun.explanation}</p>
          {dryRun.suppression && (
            <p className="text-tertiary text-sm">
              Suppressed {formatRelative(dryRun.suppression.suppressed_at)} ·
              evidence recorded:{" "}
              {dryRun.suppression.evidence_keys.join(", ") || "none"}
            </p>
          )}
          {dryRun.normalised && (
            <Link
              href={`${adminEmailMessagesPath()}?recipient=${encodeURIComponent(dryRun.normalised)}`}
              className="text-brand-secondary text-sm underline decoration-transparent transition-colors hover:decoration-current"
            >
              Ledger rows for this address
            </Link>
          )}
        </div>
      )}

      {triggers.available ? (
        <div className="border-secondary flex flex-col gap-2 border-t pt-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              color="secondary"
              size="md"
              isDisabled={triggers.smoke_blockers.length > 0}
              isLoading={smoking}
              showTextWhileLoading
              onPress={runSmoke}
            >
              Run smoke test
            </Button>
            <span className="text-tertiary text-sm">
              {webhooksExpected
                ? "Sends through the Postmark Sandbox Server, which black-holes " +
                  "the mail but fires a real webhook. The row stays Sent until " +
                  "it lands."
                : "Sends through the Postmark Sandbox Server, which black-holes " +
                  "the mail and fires a webhook. A local server cannot receive " +
                  "it, so the row stays Sent and the smoke proves the send " +
                  "half only."}
            </span>
          </div>
          {triggers.smoke_blockers.map((blocker) => (
            <Notice key={blocker} tone="warning">
              {blocker}
            </Notice>
          ))}
          <p className="text-tertiary text-sm">
            A test send goes through the ordinary guarded path, so the allowlist
            governs it like any real send.
          </p>
        </div>
      ) : (
        <div className="border-secondary flex flex-col gap-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge color="gray" size="sm">
              Sends unavailable
            </Badge>
          </div>
          <Notice tone="info">
            {triggers.reason} The send endpoints are not routed in this
            environment; a request to them 404s, the same as for non-staff.
          </Notice>
        </div>
      )}
    </Panel>
  )
}
